import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import * as Icons from "lucide-react";
import { ArrowRight, ChevronRight, Rocket, ShieldCheck, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";

function WaitlistInline({ slug, categoryName }) {
    const [email, setEmail] = useState("");
    const [done, setDone] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const onSubmit = async (e) => {
        e.preventDefault();
        if (!/.+@.+\..+/.test(email)) {
            toast.error("Please enter a valid email.");
            return;
        }
        setSubmitting(true);
        try {
            await api.joinWaitlist({ email, category_slug: slug });
            setDone(true);
            toast.success("You're on the waitlist.");
        } catch {
            toast.error("Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    if (done)
        return (
            <p className="text-sm text-indiagreen-700 inline-flex items-center gap-1.5 font-semibold">
                <CheckCircle2 className="h-4 w-4" /> You're on the waitlist
            </p>
        );

    return (
        <form
            onSubmit={onSubmit}
            className="flex flex-col sm:flex-row gap-2 max-w-md"
            data-testid={`waitlist-form-${slug}`}
        >
            <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@startup.in"
                className="flex-1 bg-white border border-line rounded-lg px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-saffron/30 focus:border-saffron/40"
                data-testid={`waitlist-email-${slug}`}
            />
            <button
                type="submit"
                disabled={submitting}
                className="btn-saffron rounded-lg px-4 py-2.5 text-sm font-semibold disabled:opacity-60"
                data-testid={`waitlist-submit-${slug}`}
            >
                {submitting ? "..." : "Notify Me"}
            </button>
        </form>
    );
}

export default function CategoryDetail() {
    const { slug } = useParams();
    const [category, setCategory] = useState(null);
    const [products, setProducts] = useState([]);
    const [related, setRelated] = useState([]);
    const [notice, setNotice] = useState(null);
    const [filters, setFilters] = useState({
        free: false,
        open_source: false,
        made_in_india: false,
    });
    const [sort, setSort] = useState("default");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        document.title = category
            ? `Indian alternatives to ${category.name} | BharatStack`
            : "BharatStack";
    }, [category]);

    useEffect(() => {
        let cancelled = false;
        setLoading(true);
        Promise.all([
            api.getCategory(slug),
            api.getCategoryProducts(slug, { ...filters, sort }),
        ])
            .then(async ([cat, prods]) => {
                if (cancelled) return;
                setCategory(cat);
                setProducts(prods);
                // Fetch the solo-1 notice (server returns null when not applicable)
                try {
                    const n = await fetch(
                        `${process.env.REACT_APP_BACKEND_URL}/api/categories/${slug}/notice`,
                    ).then((r) => r.json());
                    if (!cancelled) setNotice(n.notice);
                } catch {
                    /* ignore */
                }
                // For empty/opportunity categories, also load related categories
                if (cat?.has_opportunity && cat.opportunity?.related_slugs) {
                    const relCats = await Promise.all(
                        cat.opportunity.related_slugs.map((s) =>
                            api.getCategory(s).catch(() => null),
                        ),
                    );
                    if (!cancelled) setRelated(relCats.filter(Boolean));
                }
            })
            .catch(() => {})
            .finally(() => !cancelled && setLoading(false));
        return () => {
            cancelled = true;
        };
    }, [slug, filters, sort]);

    const Icon = (category && Icons[category.icon]) || Icons.Package;
    const FilterChip = ({ label, on, onClick, testid }) => (
        <button
            onClick={onClick}
            className={`text-sm font-semibold px-3.5 py-1.5 rounded-full border transition-colors ${
                on
                    ? "border-saffron bg-saffron text-white"
                    : "border-line bg-white text-ink hover:border-saffron/40"
            }`}
            data-testid={testid}
        >
            {label}
        </button>
    );

    return (
        <div className="max-w-7xl mx-auto px-4 lg:px-6 py-10" data-testid="category-page">
            {/* Breadcrumb */}
            <nav
                className="flex items-center gap-1 text-sm text-ink-muted mb-6"
                aria-label="Breadcrumb"
            >
                <Link to="/" className="hover:text-saffron">
                    Home
                </Link>
                <ChevronRight className="h-3.5 w-3.5" />
                <Link to="/categories" className="hover:text-saffron">
                    Categories
                </Link>
                <ChevronRight className="h-3.5 w-3.5" />
                <span className="text-ink font-medium truncate">
                    {category?.name || slug}
                </span>
            </nav>

            {/* Header */}
            <header className="flex items-start gap-5">
                <div className="h-16 w-16 rounded-2xl bg-saffron-50 text-saffron flex items-center justify-center shrink-0">
                    <Icon className="h-8 w-8" strokeWidth={2.2} />
                </div>
                <div className="min-w-0">
                    <h1 className="font-heading font-extrabold text-3xl sm:text-4xl text-ink tracking-tight">
                        {category?.name || "Category"}
                    </h1>
                    {category?.has_opportunity ? (
                        <p className="mt-2">
                            <span className="inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-widest text-white bg-saffron rounded-full px-3 py-1">
                                <Rocket className="h-3.5 w-3.5" /> Open
                                Opportunity
                            </span>
                        </p>
                    ) : (
                        <>
                            <p className="mt-2">
                                <span className="inline-flex items-center gap-1 text-xs font-semibold text-indiagreen-700 bg-indiagreen-50 border border-indiagreen-200 rounded-full px-2.5 py-0.5">
                                    {products.length} verified Indian
                                    alternatives
                                </span>
                            </p>
                            {category?.description && (
                                <p className="mt-2 text-ink-muted text-base">
                                    {category.description}
                                </p>
                            )}
                        </>
                    )}
                </div>
            </header>

            {/* Verification badge */}
            {!category?.has_opportunity && (
                <div className="mt-4 inline-flex items-center gap-2 text-xs text-ink-muted bg-white border border-line rounded-lg px-3 py-2">
                    <ShieldCheck className="h-4 w-4 text-indiagreen" />
                    <span>
                        ✅ All products on BharatStack are Indian-founded &
                        Indian-HQ'd. No foreign subsidiaries.
                    </span>
                </div>
            )}

            {/* Empty / Opportunity branch */}
            {category?.has_opportunity ? (
                <OpportunityBanner
                    category={category}
                    related={related}
                    waitlistSlot={
                        <WaitlistInline
                            slug={category.slug}
                            categoryName={category.name}
                        />
                    }
                />
            ) : (
                <>
                    {/* Filter bar */}
            <div className="mt-7 flex flex-wrap items-center gap-2">
                <FilterChip
                    label="All"
                    on={
                        !filters.free &&
                        !filters.open_source &&
                        !filters.made_in_india
                    }
                    onClick={() =>
                        setFilters({
                            free: false,
                            open_source: false,
                            made_in_india: false,
                        })
                    }
                    testid="filter-all"
                />
                <FilterChip
                    label="🆓 Free Plan"
                    on={filters.free}
                    onClick={() =>
                        setFilters((f) => ({ ...f, free: !f.free }))
                    }
                    testid="filter-free"
                />
                <FilterChip
                    label="📖 Open Source"
                    on={filters.open_source}
                    onClick={() =>
                        setFilters((f) => ({
                            ...f,
                            open_source: !f.open_source,
                        }))
                    }
                    testid="filter-open-source"
                />
                <FilterChip
                    label="🇮🇳 Made in India"
                    on={filters.made_in_india}
                    onClick={() =>
                        setFilters((f) => ({
                            ...f,
                            made_in_india: !f.made_in_india,
                        }))
                    }
                    testid="filter-india"
                />

                <div className="ml-auto flex items-center gap-2">
                    <label
                        htmlFor="sort-select"
                        className="text-sm text-ink-muted"
                    >
                        Sort
                    </label>
                    <select
                        id="sort-select"
                        value={sort}
                        onChange={(e) => setSort(e.target.value)}
                        className="text-sm bg-white border border-line rounded-lg px-3 py-1.5 focus:outline-none focus:ring-2 focus:ring-saffron/30"
                        data-testid="sort-select"
                    >
                        <option value="default">Default</option>
                        <option value="upvotes">Most Upvoted</option>
                        <option value="newest">Newest</option>
                        <option value="alpha">A → Z</option>
                    </select>
                </div>
            </div>

            {/* Product list */}
            <div className="mt-6 grid grid-cols-1 gap-4">
                {loading && (
                    <div className="text-center py-16 text-ink-muted">
                        Loading...
                    </div>
                )}
                {!loading && products.length === 0 && (
                    <div className="text-center py-16 text-ink-muted bg-white border border-line rounded-2xl">
                        No products match your filters yet. Try removing a
                        filter.
                    </div>
                )}
                {products.map((p) => (
                    <ProductCard key={p.id} product={p} />
                ))}
                {/* Solo "only 1 alternative" notice */}
                {!loading && products.length === 1 && notice && (
                    <div
                        className="bg-saffron-50 border border-saffron/30 rounded-2xl px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3"
                        data-testid="solo-notice"
                    >
                        <Rocket className="h-5 w-5 text-saffron shrink-0" />
                        <p className="text-sm text-ink flex-1 leading-relaxed">
                            {notice}
                        </p>
                        <Link
                            to={`/submit?category=${slug}`}
                            className="text-sm font-semibold text-saffron-700 hover:underline whitespace-nowrap"
                            data-testid="solo-notice-submit"
                        >
                            Submit it →
                        </Link>
                    </div>
                )}
            </div>
            </>
            )}
        </div>
    );
}

function OpportunityBanner({ category, related, waitlistSlot }) {
    const o = category.opportunity || {};
    return (
        <div className="mt-6" data-testid={`opportunity-banner-${category.slug}`}>
            <div className="bg-gradient-to-br from-saffron to-saffron-600 text-white rounded-3xl overflow-hidden shadow-xl">
                <div className="p-6 sm:p-10">
                    <div className="text-5xl">{o.icon_emoji || "🚀"}</div>
                    <h2 className="mt-4 font-heading font-extrabold text-2xl sm:text-3xl tracking-tight">
                        {o.heading}
                    </h2>
                    <p className="mt-2 font-heading font-bold text-lg sm:text-xl text-white/95">
                        {o.subheading}
                    </p>
                    <p className="mt-5 text-base text-white/95 leading-relaxed max-w-3xl">
                        {o.body}
                    </p>
                    {o.bullets && (
                        <ul className="mt-5 space-y-2 max-w-3xl">
                            {o.bullets.map((b, i) => (
                                <li
                                    key={i}
                                    className="flex items-start gap-2 text-sm text-white/95"
                                >
                                    <span className="mt-0.5">•</span>
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    <div className="mt-7 flex flex-wrap items-center gap-3">
                        <Link
                            to={`/submit?category=${category.slug}`}
                            className="inline-flex items-center gap-1.5 bg-indiagreen text-white px-5 py-3 rounded-lg font-semibold text-sm hover:bg-indiagreen-700 transition-colors"
                            data-testid={`opp-build-cta-${category.slug}`}
                        >
                            I'm Building This
                            <ArrowRight className="h-4 w-4" />
                        </Link>
                        <Link
                            to="/opportunities"
                            className="text-sm font-semibold text-white/90 hover:text-white"
                        >
                            See all opportunities →
                        </Link>
                    </div>
                </div>
            </div>

            <div className="mt-6 bg-white border border-line rounded-2xl p-5 sm:p-6">
                <p className="text-sm font-semibold text-ink mb-2">
                    Get notified when an Indian {category.name.toLowerCase()} launches:
                </p>
                {waitlistSlot}
            </div>

            {related && related.length > 0 && (
                <div className="mt-10">
                    <h3 className="font-heading font-bold text-ink text-xl">
                        Related categories with Indian alternatives
                    </h3>
                    <div className="mt-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
                        {related.map((r) => (
                            <Link
                                key={r.slug}
                                to={`/category/${r.slug}`}
                                className="bg-white border border-line rounded-xl p-4 card-lift block"
                                data-testid={`related-cat-${r.slug}`}
                            >
                                <div className="font-heading font-bold text-ink">
                                    {r.name}
                                </div>
                                <div className="mt-1 text-xs text-ink-muted">
                                    {r.product_count} verified Indian
                                    alternatives
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
